import React, { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc, orderBy, onSnapshot, type Timestamp } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { db } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, User } from '../../../utils/firebase';
import { QRCodeSVG } from 'qrcode.react';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import StoreItemCardForStudent from '../../../components/ui/StoreItemCardForStudent';
import type { CharacterLayerSlot, CharacterLoadout } from '../../../types/character';
import { FEATURE_CHARACTER_COSMETIC } from '../../../config/featureFlags';
import { canWearCharacterShopOutfit, getCharacterPreset } from '../../../utils/characterPresets';
import { PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY, type PetPlayActionType } from '../../../types/petStore';
import {
  countItemsByStoreType,
  getCategoryFilterOptions,
  getStoreTypeLabel,
  getVisibleStoreTypes,
  itemBelongsToStoreType,
  parseStoreTypeParam,
  storeTypeToQueryValue,
  type StoreItemCategoryId,
  type StoreTypeId,
} from '../../../utils/storeTypes';

// 타입 가드 함수, isStudent는 함수 이름
const isStudent = (user: User): user is Student => {
  return user.role === 'student';
};
/* (user: User)는 매개변수로, User타입의 user를 받는다는 이 함수가 true를 반환하면
TypeScript 컴파일러가 user를 student타입으로 간주할 수 있게함.
*/


interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  targetType: 'class' | 'subject';
  itemType: 'point' | 'stamp';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stock: number;
  isLimited: boolean;
  salePeriod?: {
    start: Date;
    end: Date;
  };
  isUnlimitedStock: boolean;
  qrData?: string;
  imageUrl?: string;
  itemCategory: 'usage' | 'qr' | 'seat' | 'seatReservation' | 'seatTakeover' | 'collectible' | 'gacha' | 'cosmetic' | 'furniture' | 'roomSurface' | typeof PET_CONSUMABLE_CATEGORY | typeof PET_PLAY_TICKET_CATEGORY;
  petPlayAction?: PetPlayActionType;
  characterSlot?: CharacterLayerSlot;
  characterOutfitId?: string;
  gachaItems?: Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>;
  gachaAnimationType?: 'scratch' | 'card' | 'maru';
  qrCodeExpiry?: number;
  teacherId: string;
  subjectId?: string;
}

interface StudentData {
  points: number;
  teacherId: string;
  subjectTeachers?: string[];
  stamps?: Array<{
    subjectId: string;
    stampCount: number;
  }>;
  class: number;
  classId: string;
  grade: number;
  number: number;
  name: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  pointItems: number;
  stampItems: number;
  role: string;
  roles: string[];
  schoolId: string;
  uid: string;
  email: string;
  username: string;
  loginType: string;
  items?: { [key: string]: {
    itemId?: string;
    quantity: number;
    name: string;
    description: string;
    type: string;
    targetType: string;
    itemCategory: string;
    imageUrl?: string;
    purchasedAt: Timestamp;
    lastUsedAt: Timestamp | null;
  } };
  purchaseHistory?: Array<{
    itemId: string;
    itemName: string;
    price: number;
    purchasedAt: Date;
    subjectId: string;
  }>;
  /** 보유 코스메틱 상품 id(storeItems 문서 id) */
  characterInventory?: string[];
  characterPreset?: string;
  /** 슬롯별 장착 중인 상품 id */
  characterLoadout?: CharacterLoadout;
}

interface StoreNotice {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  targetType: 'class' | 'subject';
  teacherId: string;
  classId?: string;
  subjectId?: string;
}


const ClassItemShop: React.FC = () => {
  console.log('ItemShop 컴포넌트 마운트');
  
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  console.log('ItemShop - currentUser:', currentUser);
  console.log('ItemShop - currentUser type:', typeof currentUser);
  console.log('ItemShop - currentUser constructor:', currentUser?.constructor?.name);
  console.log('ItemShop - currentUser role:', currentUser?.role);
  console.log('ItemShop - currentUser schoolId:', currentUser?.schoolId);
  console.log('ItemShop - currentUser classId:', currentUser?.classId);
  console.log('ItemShop - isStudent check:', currentUser ? isStudent(currentUser) : false);
  
  const [studentPoints, setStudentPoints] = useState<number>(0);
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [items, setItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [successDialog, setSuccessDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StoreItem | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [qrDialog, setQrDialog] = useState(false);
  const [selectedQrItem, setSelectedQrItem] = useState<StoreItem | null>(null);
  const [subjectStamps, setSubjectStamps] = useState<{ [key: string]: number }>({});
  const [notices, setNotices] = useState<StoreNotice[]>([]);
  const [selectedStoreType, setSelectedStoreType] = useState<StoreTypeId | null>(() =>
    parseStoreTypeParam(searchParams.get('store'))
  );
  const [filterCategory, setFilterCategory] = useState<'all' | StoreItemCategoryId>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  
  // 페이지당 아이템 수 (데스크톱에서는 5개)
  const itemsPerPage = 5;

  useEffect(() => {
    const fromUrl = parseStoreTypeParam(searchParams.get('store'));
    if (fromUrl !== selectedStoreType) {
      setSelectedStoreType(fromUrl);
      setFilterCategory('all');
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL 변경만 반영
  }, [searchParams]);

  const handleEnterStore = (storeType: StoreTypeId) => {
    setSelectedStoreType(storeType);
    setFilterCategory('all');
    setCurrentPage(1);
    const next = new URLSearchParams(searchParams);
    next.set('store', storeTypeToQueryValue(storeType));
    setSearchParams(next, { replace: true });
  };

  const handleBackToStoreHub = () => {
    setSelectedStoreType(null);
    setFilterCategory('all');
    setCurrentPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('store');
    setSearchParams(next, { replace: true });
  };

  // 학생 데이터 가져오기
  useEffect(() => {
    console.log('ItemShop - useEffect - currentUser:', currentUser);
    console.log('ItemShop - useEffect - currentUser type:', typeof currentUser);
    console.log('ItemShop - useEffect - currentUser constructor:', currentUser?.constructor?.name);
    console.log('ItemShop - useEffect - isStudent check:', currentUser ? isStudent(currentUser) : false);
    console.log('ItemShop - useEffect - currentUser schoolId:', currentUser?.schoolId);
    console.log('ItemShop - useEffect - currentUser classId:', currentUser?.classId);
    
    if (!currentUser || !isStudent(currentUser)) {
      console.error('Invalid user type:', currentUser);
      setError('학생 정보를 불러올 수 없습니다.');
      setLoading(false);
      return;
    }

    const student = currentUser;
    console.log('Student type check passed');
    console.log('Full student data:', {
      uid: student.uid,
      schoolId: student.schoolId,
      classId: student.classId,
      role: student.role,
      points: student.points
    });

    if (!student.schoolId || !student.uid || !student.classId) {
      console.error('Missing required student data:', {
        schoolId: student.schoolId,
        uid: student.uid,
        classId: student.classId
      });
      setError('학생 정보가 없습니다.');
      setLoading(false);
      return;
    }

    console.log('Attempting to fetch student data with path:', `schools/${student.schoolId}/classes/${student.classId}/students/${student.uid}`);
    const studentRef = doc(db, `schools/${student.schoolId}/classes/${student.classId}/students`, student.uid as string);

    const unsubscribe = onSnapshot(studentRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        console.log('Firestore student data loaded:', data);
        setStudentData(data as StudentData);
        setStudentPoints(data.points || 0);
        
        // stamps 데이터 처리 수정
        if (data.stamps && Array.isArray(data.stamps)) {
          const stampsObj: { [key: string]: number } = {};
          for (const stamp of data.stamps) {
            if (stamp.subjectId) {
              // stampCount를 직접 사용
              stampsObj[stamp.subjectId] = stamp.stampCount || 0;
              console.log(`도장 데이터 처리 - 과목: ${stamp.subjectId}, 개수: ${stamp.stampCount}`);
            }
          }
          console.log('원본 도장 데이터:', data.stamps);
          console.log('처리된 도장 데이터:', stampsObj);
          setSubjectStamps(stampsObj);
        } else {
          console.log('도장 데이터가 없거나 배열이 아닙니다:', data.stamps);
          setSubjectStamps({});
        }
      } else {
        console.error('No student document found in Firestore');
        setError('학생 정보를 찾을 수 없습니다.');
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching student data:', error);
      setError('학생 데이터를 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 아이템 목록 가져오기
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser)) return;

    const student = currentUser;
    let subjectUnsubscribes: (() => void)[] = [];

    // 학급 상점 아이템 구독
    const classItemsUnsubscribe = onSnapshot(
      collection(db, `schools/${student.schoolId}/classes/${student.classId}/storeItems`),
      (classSnapshot) => {
        const classItems = classSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          targetType: 'class'
        })) as StoreItem[];

        // 교과별 실시간 구독
        if (studentData?.stamps && Array.isArray(studentData.stamps)) {
          // 기존 구독 해제
          subjectUnsubscribes.forEach(unsub => unsub());
          subjectUnsubscribes = [];

          let allSubjectItems: StoreItem[] = [];
          let loadedCount = 0;
          const totalSubjects = studentData.stamps.length;

          studentData.stamps.forEach(stamp => {
            if (!stamp.subjectId) {
              loadedCount++;
              if (loadedCount === totalSubjects) {
                setItems([...classItems, ...allSubjectItems]);
              }
              return;
            }
            const subjectStoreRef = collection(db, `schools/${student.schoolId}/subjects/${stamp.subjectId}/subjectstoreItems`);
            const unsub = onSnapshot(subjectStoreRef, (subjectSnapshot) => {
              const subjectItems = subjectSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                targetType: 'subject',
                subjectId: stamp.subjectId
              })) as StoreItem[];
              allSubjectItems = allSubjectItems.filter(item => item.subjectId !== stamp.subjectId).concat(subjectItems);
              loadedCount++;
              if (loadedCount === totalSubjects) {
                setItems([...classItems, ...allSubjectItems]);
              }
            });
            subjectUnsubscribes.push(unsub);
          });
        } else {
          setItems(classItems);
        }
      },
      (error) => {
        console.error('학급 상점 아이템 구독 에러:', error);
        setError('학급 상점 아이템을 불러오는데 실패했습니다.');
      }
    );

    return () => {
      classItemsUnsubscribe();
      subjectUnsubscribes.forEach(unsub => unsub());
    };
  }, [currentUser]);

  // 공지사항 가져오기
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser)) return;

    const student = currentUser;
    let classNoticesUnsubscribe = () => {};
    let subjectNoticesUnsubscribe = () => {};

    try {
      // 학급 공지사항 구독
      const classNoticeRef = collection(db, `schools/${student.schoolId}/classes/${student.classId}/storeNotices`);
      const classNoticeQuery = query(
        classNoticeRef,
        where('isActive', '==', true),
        orderBy('updatedAt', 'desc')
      );

      classNoticesUnsubscribe = onSnapshot(classNoticeQuery, (classSnapshot) => {
        const classNotices = classSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        })) as StoreNotice[];

        setNotices(prevNotices => {
          const subjectNotices = prevNotices.filter(notice => notice.targetType === 'subject');
          return [...classNotices, ...subjectNotices];
        });
      });

      // 교과 공지사항 구독
      if (studentData?.stamps) {
        const subjectIds = studentData.stamps.map(stamp => stamp.subjectId);
        const subjectNoticesPromises = subjectIds.map(async (subjectId) => {
          if (!subjectId) return;
          
          const subjectNoticeRef = collection(db, `schools/${student.schoolId}/subjects/${subjectId}/subjectstoreNotices`);
          const subjectNoticeQuery = query(
            subjectNoticeRef,
            where('isActive', '==', true),
            orderBy('updatedAt', 'desc')
          );

          return onSnapshot(subjectNoticeQuery, (subjectSnapshot) => {
            const subjectNotices = subjectSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              updatedAt: doc.data().updatedAt?.toDate() || new Date()
            })) as StoreNotice[];

            setNotices(prevNotices => {
              const classNotices = prevNotices.filter(notice => notice.targetType === 'class');
              return [...classNotices, ...subjectNotices];
            });
          });
        });

        Promise.all(subjectNoticesPromises).then(unsubscribes => {
          subjectNoticesUnsubscribe = () => unsubscribes.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
              unsubscribe();
            }
          });
        });
      }
    } catch (err) {
      console.error('Error setting up notice listeners:', err);
    }

    return () => {
      classNoticesUnsubscribe();
      subjectNoticesUnsubscribe();
    };
  }, [currentUser, studentData]);


  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 공지사항이 변경될 때 인덱스 리셋
  useEffect(() => {
    if (notices.length > 0 && currentNoticeIndex >= notices.length) {
      setCurrentNoticeIndex(0);
    }
  }, [notices, currentNoticeIndex]);

  // 디버깅용 useEffect
  useEffect(() => {
    if (currentUser && isStudent(currentUser)) {
      console.log('Current studentPoints:', studentPoints);
      console.log('Current studentData:', studentData);
    }
  }, [studentPoints, studentData, currentUser]);

  if (!currentUser || !isStudent(currentUser)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px', border: '1px solid #fcc' }}>
          학생 정보를 불러올 수 없습니다.
        </div>
      </div>
    );
  }

  const student = currentUser;

  const canPurchaseItem = (item: StoreItem): boolean => {
    if (!studentData) return false;
    if (item.itemCategory === 'cosmetic' && !canWearCharacterShopOutfit(item.characterOutfitId, getCharacterPreset(studentData.characterPreset).appearance)) return false;
    if (!FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic') return false;

    // 한 번만 구매 가능한 아이템(isLimited) 체크
    if (item.isLimited && studentData.items && studentData.items[item.id]?.quantity > 0) {
      return false;
    }
    if (item.itemCategory === 'furniture' && Object.values(studentData.items || {}).some((owned) => owned.itemId === item.id)) return false;
    if (item.itemCategory === 'roomSurface' && Object.values(studentData.items || {}).some((owned) => owned.itemId === item.id)) return false;
    if (
      FEATURE_CHARACTER_COSMETIC &&
      item.itemCategory === 'cosmetic' &&
      item.isLimited &&
      studentData.characterInventory?.includes(item.id)
    ) {
      return false;
    }

    // 도장 아이템인 경우
    if (item.itemType === 'stamp' && item.subjectId) {
      const stampCount = subjectStamps[item.subjectId] || 0;
      return stampCount >= item.price;
    }

    // 포인트 아이템인 경우
    return (
      studentPoints >= item.price &&
      (item.isUnlimitedStock || item.stock > 0) &&
      (!item.salePeriod || (
        new Date() >= new Date(item.salePeriod.start) &&
        new Date() <= new Date(item.salePeriod.end)
      ))
    );
  };


  // 구매 버튼 클릭 시 확인 다이얼로그 열기
  const handlePurchaseClick = (item: StoreItem) => {
    setSelectedItem(item);
    setConfirmDialog(true);
  };

  // 실제 구매 처리
  const handlePurchase = async (item: StoreItem) => {
    // 확인 다이얼로그 닫기
    setConfirmDialog(false);
    
    if (!studentData || !currentUser || !isStudent(currentUser)) {
      setError('구매할 수 없는 아이템입니다.');
      return;
    }

    // 한 번만 구매 가능한 아이템 체크
    if (item.isLimited && studentData.items && studentData.items[item.id]?.quantity > 0) {
      setError('이 아이템은 한 번만 구매할 수 있습니다.');
      return;
    }
    if (FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic') {
      if (!canWearCharacterShopOutfit(item.characterOutfitId, getCharacterPreset(studentData.characterPreset).appearance)) {
        setError('여학생 캐릭터 전용 복장입니다. 옷장에서 캐릭터를 변경한 후 구매해 주세요.');
        return;
      }
      if (!item.characterOutfitId && (!item.characterSlot || !item.imageUrl)) {
        setError('캐릭터 복장 상품 정보가 올바르지 않습니다. 담임 선생님께 문의하세요.');
        return;
      }
      if (item.isLimited && studentData.characterInventory?.includes(item.id)) {
        setError('이미 보유한 캐릭터 복장입니다.');
        return;
      }
    }
    if (item.itemCategory === 'furniture' && Object.values(studentData.items || {}).some((owned) => owned.itemId === item.id)) {
      setError('이미 보유한 가구입니다.');
      return;
    }
    if (item.itemCategory === 'roomSurface' && Object.values(studentData.items || {}).some((owned) => owned.itemId === item.id)) { setError('이미 보유한 벽지 또는 바닥재입니다.'); return; }
    if (!FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic') {
      setError('현재 캐릭터 복장 상품을 구매할 수 없습니다.');
      return;
    }

    try {
      setLoading(true);
      const purchaseSchoolId = currentUser.schoolId;
      const purchaseClassId = currentUser.classId;
      if (!purchaseSchoolId || !purchaseClassId) {
        setError('학교·학급 정보가 없어 구매할 수 없습니다.');
        setLoading(false);
        return;
      }
      const functions = getFunctions(getApp());
      const purchase = httpsCallable<
        { schoolId: string; classId: string; itemId: string; targetType: string; subjectId?: string },
        { itemName?: string }
      >(functions, 'purchaseStoreItem');
      await purchase({
        schoolId: purchaseSchoolId,
        classId: purchaseClassId,
        itemId: item.id,
        targetType: item.itemType === 'stamp' ? 'subject' : 'class',
        subjectId: item.subjectId,
      });

      setSuccessDialog({ open: true, message: `${item.name}을(를) 구매했습니다.` });
    } catch (err) {
      console.error('아이템 구매 중 오류:', err);
      setError(err instanceof Error ? err.message : '아이템 구매에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseItem = (item: StoreItem) => {
    setSelectedQrItem(item);
    setQrDialog(true);
  };

  const generateQrData = (item: StoreItem, studentId: string) => {
    return JSON.stringify({
      itemId: item.id,
      studentId: studentId,
      timestamp: Date.now(),
      type: item.itemType,
      name: item.name
    });
  };


  // 교과 이름 변환 함수 추가
  const getSubjectName = (subjectId: string): string => {
    const subjectMap: { [key: string]: string } = {
      'art': '미술',
      'math': '수학',
      'science': '과학',
      'english': '영어',
      'korean': '국어',
      'social': '사회',
      'history': '역사',
      'music': '음악',
      'pe': '체육',
      'home': '가정',
      'tech': '기술',
      'ethics': '도덕',
      'computer': '컴퓨터',
      'chinese': '중국어',
      'japanese': '일본어',
      'german': '독일어',
      'french': '프랑스어',
      'spanish': '스페인어',
      'russian': '러시아어',
      'vietnamese': '베트남어',
      'arabic': '아랍어',
      'other': '기타'
    };
    
    return subjectMap[subjectId] || subjectId;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px', border: '1px solid #fcc' }}>
          {error}
        </div>
      </div>
    );
  }

  const classItems = items.filter(item => item.targetType === 'class' && item.isActive);
  const subjectItems = items.filter(item => item.targetType === 'subject' && item.isActive);
  
  // 필터링된 아이템 (학급 + 교과 모두 포함)
  const allItems = [...classItems, ...subjectItems];
  const allItemsForShop = FEATURE_CHARACTER_COSMETIC
    ? allItems
    : allItems.filter((item) => item.itemCategory !== 'cosmetic');
  const visibleStoreTypes = getVisibleStoreTypes();
  const isStoreHub = selectedStoreType === null;
  const categoryFilterOptions = selectedStoreType
    ? getCategoryFilterOptions(selectedStoreType)
    : [];
  const effectiveFilter =
    !FEATURE_CHARACTER_COSMETIC && filterCategory === 'cosmetic' ? 'all' : filterCategory;

  const matchesCategoryFilter = (itemCategory: string, filter: 'all' | StoreItemCategoryId) => {
    if (filter === 'all') return true;
    if (filter === 'seatReservation' && (itemCategory === 'seatReservation' || itemCategory === 'seat')) {
      return true;
    }
    return itemCategory === filter;
  };

  const filteredItems = !selectedStoreType
    ? []
    : allItemsForShop.filter((item) => {
        if (!itemBelongsToStoreType(item.itemCategory, selectedStoreType)) return false;
        return matchesCategoryFilter(item.itemCategory, effectiveFilter);
      });
  
  // 페이지네이션 (데스크톱에서만)
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = isMobile ? filteredItems : filteredItems.slice(startIndex, endIndex);

  const handleFilterChange = (category: 'all' | StoreItemCategoryId) => {
    setFilterCategory(category);
    setCurrentPage(1);
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const renderStoreHub = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div
        style={{
          color: 'var(--Color-text-normal, #161616)',
          fontSize: isMobile ? '16px' : '18px',
          fontFamily: 'pretendard',
          fontWeight: 600,
          lineHeight: '150%',
        }}
      >
        들어가고 싶은 상점을 선택하세요
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: '12px',
        }}
      >
        {visibleStoreTypes.map(({ id, label, description }) => {
          const count = countItemsByStoreType(allItemsForShop, id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleEnterStore(id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '20px',
                textAlign: 'left',
                background: 'var(--Color-background-normal, white)',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '16px',
                cursor: 'pointer',
                fontFamily: 'pretendard',
              }}
            >
              <span
                style={{
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '27px',
                }}
              >
                {label}
              </span>
              <span
                style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}
              >
                {description}
              </span>
              <span
                style={{
                  marginTop: '4px',
                  color: 'var(--Color-fill-brand-color, #FF6F0F)',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}
              >
                판매 중 {count}개
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStoreHeader = () => {
    if (!selectedStoreType) return null;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={handleBackToStoreHub}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '13px',
            fontFamily: 'pretendard',
            fontWeight: 500,
            lineHeight: '20px',
            width: 'fit-content',
          }}
        >
          <LeftArrowIcon width={16} height={16} color="#989898" />
          상점 선택
        </button>
        <div
          style={{
            color: 'var(--Color-text-strong, #0D0D0D)',
            fontSize: isMobile ? '20px' : '24px',
            fontFamily: 'pretendard',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          {getStoreTypeLabel(selectedStoreType)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: 0,
      position: 'relative', 
      background: 'transparent', 
      overflow: isMobile ? 'auto' : 'hidden',
      overflowX: 'hidden',
      paddingLeft: '0',
      paddingRight: '0',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {error && (
        <div style={{ 
          marginBottom: '16px', 
          position: 'relative', 
          top: 'auto', 
          left: 0, 
          right: 0, 
          zIndex: 1000, 
          padding: '12px', 
          background: '#fee', 
          color: '#c33', 
          borderRadius: '4px', 
          border: '1px solid #fcc',
          marginTop: '0'
        }}>
          {error}
        </div>
      )}

      <div style={{
        width: '1280px',
        maxWidth: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: isMobile ? '0 20px' : '0',
        overflowX: 'hidden'
      }}>
        {/* 상점 및 보유 포인트 영역 */}
        <div style={{ 
          flexShrink: 0,
          paddingTop: '0',
          paddingBottom: '0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <h1 style={{ 
              color: '#161616', 
              fontSize: isMobile ? '24px' : '32px', 
              fontFamily: 'pretendard', 
              fontWeight: '600', 
              lineHeight: isMobile ? '36px' : '48px',
              margin: 0
            }}>
              상점
            </h1>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
            }}>
              <span style={{ 
                color: '#161616', 
                fontSize: isMobile ? '14px' : '18px', 
                fontFamily: 'pretendard', 
                fontWeight: '600', 
                lineHeight: isMobile ? '21px' : '27px',
              }}>
                나의 보유 포인트
              </span>
              <span style={{ 
                color: '#FF6F0F', 
                fontSize: isMobile ? '16px' : '18px', 
                fontFamily: 'pretendard', 
                fontWeight: '600', 
                lineHeight: isMobile ? '24px' : '27px',
              }}>
                {studentPoints} P
              </span>
            </div>
          </div>
        </div>

        {/* 콘텐츠 영역 */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '52px',
          flex: '0 0 auto'
        }}>
          {/* 공지사항 섹션 */}
          {isMobile ? (
            // 모바일 버전: 공지사항 하나씩 표시, 제목/더보기 없음
            <div style={{ 
              paddingLeft: '16px', 
              paddingRight: '16px', 
              paddingTop: '16px', 
              paddingBottom: '16px', 
              background: 'white', 
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ 
                  color: '#4C4C4C', 
                  fontSize: '14px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '500', 
                  lineHeight: '21px',
                  flex: 1
                }}>
                  {notices.length > 0 ? (notices[currentNoticeIndex]?.content || '공지사항이 없습니다.') : '공지사항이 없습니다.'}
                </span>
                {notices.length > 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => setCurrentNoticeIndex(Math.max(0, currentNoticeIndex - 1))}
                      disabled={currentNoticeIndex === 0}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        background: currentNoticeIndex === 0 ? '#E9E9E9' : '#F5F5F5',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: currentNoticeIndex === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: currentNoticeIndex === 0 ? '#989898' : '#161616',
                        fontSize: '18px'
                      }}
                    >
                      ←
                    </button>
                    <span style={{
                      color: '#989898',
                      fontSize: '12px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '18px',
                      minWidth: '40px',
                      textAlign: 'center'
                    }}>
                      {currentNoticeIndex + 1} / {notices.length}
                    </span>
                    <button
                      onClick={() => setCurrentNoticeIndex(Math.min(notices.length - 1, currentNoticeIndex + 1))}
                      disabled={currentNoticeIndex === notices.length - 1}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        background: currentNoticeIndex === notices.length - 1 ? '#E9E9E9' : '#F5F5F5',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: currentNoticeIndex === notices.length - 1 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: currentNoticeIndex === notices.length - 1 ? '#989898' : '#161616',
                        fontSize: '18px'
                      }}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
              {notices.length > 0 && notices[currentNoticeIndex] && (
                <span style={{ 
                  color: '#989898', 
                  fontSize: '12px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '500', 
                  lineHeight: '18px'
                }}>
                  {formatDate(notices[currentNoticeIndex].updatedAt)}
                </span>
              )}
            </div>
          ) : (
            // 데스크톱 버전: 기존 레이아웃
            <div style={{ 
              paddingLeft: '28px', 
              paddingRight: '28px', 
              paddingTop: '16px', 
              paddingBottom: '16px', 
              background: 'white', 
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                gap: '40px',
                flexDirection: 'row'
              }}>
                <div style={{ 
                  flex: '1 1 0', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '61px',
                  flexDirection: 'row',
                  width: 'auto'
                }}>
                  <h2 style={{ 
                    color: '#161616', 
                    fontSize: '20px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '600', 
                    lineHeight: '30px',
                    margin: 0
                  }}>
                    공지사항
                  </h2>
                  <div style={{ 
                    flex: '1 1 0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '187px',
                    flexDirection: 'row',
                    width: 'auto'
                  }}>
                    <span style={{ 
                      flex: '1 1 0', 
                      color: '#4C4C4C', 
                      fontSize: '16px', 
                      fontFamily: 'pretendard', 
                      fontWeight: '500', 
                      lineHeight: '24px' 
                    }}>
                      {notices[0]?.content || '공지사항이 없습니다.'}
                    </span>
                    {notices[0] && (
                      <span style={{ 
                        color: '#4C4C4C', 
                        fontSize: '16px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '600', 
                        lineHeight: '24px',
                        flexShrink: 0
                      }}>
                        {formatDate(notices[0].updatedAt)}
                      </span>
                    )}
                  </div>
                </div>
                <button style={{ 
                  paddingLeft: '16px', 
                  paddingRight: '16px', 
                  paddingTop: '12px', 
                  paddingBottom: '12px', 
                  background: '#E9E9E9', 
                  borderRadius: '8px',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#D9D9D9'}
                onMouseOut={(e) => e.currentTarget.style.background = '#E9E9E9'}
                >
                  더 보기
                  <span style={{ width: '20px', height: '20px', display: 'inline-block' }}>+</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 아이템 목록 섹션 */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '28px',
          flex: '0 0 auto'
        }}>
          {isStoreHub ? (
            renderStoreHub()
          ) : (
            <>
          {renderStoreHeader()}
          {/* 포인트 및 필터 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            gap: '20px',
            flexDirection: isMobile ? 'column' : 'row',
            flexShrink: 0
          }}>
            {/* 필터 */}
            {categoryFilterOptions.length > 0 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              overflowX: isMobile ? 'auto' : 'visible',
              overflowY: 'hidden',
              width: isMobile ? '100%' : 'auto',
              paddingBottom: '0',
              WebkitOverflowScrolling: isMobile ? 'touch' : 'auto',
              scrollbarWidth: isMobile ? 'none' : 'auto',
              msOverflowStyle: isMobile ? 'none' : 'auto'
            } as React.CSSProperties}>
              {isMobile && (
                <style>{`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
              )}
              {categoryFilterOptions.map(({ key, label }) => {
                const isSelected = effectiveFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleFilterChange(key)}
                    style={{
                      height: '32px',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      background: isSelected ? '#293044' : 'white',
                      borderRadius: '8px',
                      border: isSelected ? 'none' : '1px solid #F0F0F0',
                      color: isSelected ? 'white' : '#161616',
                      fontSize: '12px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected) e.currentTarget.style.background = '#F5F5F5';
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) e.currentTarget.style.background = 'white';
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            )}
          </div>

          {/* 아이템 그리드 */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
            gap: '16px',
            width: '100%'
          }}>
            {paginatedItems.length === 0 ? (
              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: '40px 16px',
                  textAlign: 'center',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                }}
              >
                판매 중인 아이템이 없습니다.
              </div>
            ) : (
              paginatedItems.map((item) => (
              <div key={item.id} style={{ 
                display: 'flex', 
                justifyContent: 'center',
                width: '100%'
              }}>
                <StoreItemCardForStudent
                  item={item as any}
                  isMobile={isMobile}
                  onPurchase={handlePurchaseClick as any}
                  canPurchase={canPurchaseItem(item)}
                  formatDate={formatDate}
                />
              </div>
              ))
            )}
          </div>

          {/* 페이지네이션 (데스크톱에서만) */}
          {!isMobile && totalPages > 0 && (
            <div style={{
              paddingTop: '20px',
              paddingBottom: '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'none',
                    border: 'none',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LeftArrowDoubleIcon 
                    width={24} 
                    height={24} 
                    color={currentPage === 1 ? '#989898' : '#161616'} 
                  />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'none',
                    border: 'none',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LeftArrowIcon 
                    width={20} 
                    height={21} 
                    color={currentPage === 1 ? '#989898' : '#161616'} 
                  />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage === 1) {
                    pageNum = i + 1;
                  } else if (currentPage === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = currentPage - 1 + i;
                  }
                  const isSelected = currentPage === pageNum;
                  return (
                    <div
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum as number)}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: isSelected ? '#161616' : '#989898',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '14px'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#F5F5F5'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {pageNum}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'none',
                    border: 'none',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RightArrowIcon 
                    width={20} 
                    height={21} 
                    color={currentPage === totalPages ? '#989898' : '#161616'} 
                  />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'none',
                    border: 'none',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RightArrowDoubleIcon 
                    width={24} 
                    height={24} 
                    color={currentPage === totalPages ? '#989898' : '#161616'} 
                  />
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* 구매 확인 다이얼로그 */}
      {confirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }} onClick={() => setConfirmDialog(false)}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>아이템 구매 확인</h2>
            {selectedItem?.imageUrl && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <img 
                  src={selectedItem.imageUrl} 
                  alt={selectedItem.name || '아이템'} 
                  style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }} 
                />
              </div>
            )}
            <p>
              {selectedItem?.name || '아이템'}을(를) 구매하시겠습니까?
            </p>
            <p style={{ color: '#666', marginTop: '8px' }}>
              가격: {selectedItem?.price || 0} {selectedItem?.itemType === 'point' ? '포인트' : '도장'}
            </p>
            <p style={{ color: '#666' }}>
              현재 보유: {selectedItem?.itemType === 'point' ? studentPoints : (selectedItem ? subjectStamps[selectedItem.id] || 0 : 0)} {selectedItem?.itemType === 'point' ? '포인트' : '도장'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button onClick={() => setConfirmDialog(false)} style={{ padding: '8px 16px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>
                취소
              </button>
              <button onClick={() => selectedItem && handlePurchase(selectedItem)} style={{ padding: '8px 16px', border: 'none', background: '#293044', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
                구매하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 구매 성공 팝업 */}
      {successDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }} onClick={() => setSuccessDialog({ open: false, message: '' })}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#4CAF50',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 24px',
              fontSize: '32px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: '600',
              color: '#161616'
            }}>
              구매 완료
            </h2>
            <p style={{ 
              margin: '0 0 24px 0',
              fontSize: '16px',
              color: '#666',
              lineHeight: '24px'
            }}>
              {successDialog.message}
            </p>
            <button 
              onClick={() => setSuccessDialog({ open: false, message: '' })}
              style={{ 
                padding: '12px 32px', 
                border: 'none', 
                background: '#FF6F0F', 
                color: 'white', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                width: '100%'
              }}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* QR 코드 다이얼로그 */}
      {qrDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }} onClick={() => setQrDialog(false)}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: '20px' }}>아이템 사용</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
              <h3 style={{ marginTop: 0, fontSize: '18px' }}>
                {selectedQrItem?.name}
              </h3>
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '16px' }}>
                교사에게 QR 코드를 보여주세요
              </p>
              <div style={{ padding: '16px', background: 'white', borderRadius: '4px' }}>
                {student && selectedQrItem && student.uid ? (
                  <QRCodeSVG
                    value={generateQrData(selectedQrItem, student.uid)}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                ) : null}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button onClick={() => setQrDialog(false)} style={{ padding: '8px 16px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClassItemShop; 
